import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import { type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import cn from '@/utils/cn';

interface SalesTableStateProps {
  variant: 'empty' | 'error';
  title: string;
  description?: string;
  onRetry?: () => void;
  // Optional call-to-action buttons, shown on the empty variant (e.g. Add /
  // Import) so a fresh table guides the user to the next step.
  action?: ReactNode;
  // Extra classes for the root, e.g. `h-full` so the message centers within the
  // full table height/width (Fix 6).
  className?: string;
}

// Shared, professional placeholder for the Leads / Sales tables' empty and error
// states (replaces the bare red "Failed to load leads." text). Rendered as the
// table container's child so it can fill the full available height and center.
export function SalesTableState({
  variant,
  title,
  description,
  onRetry,
  action,
  className,
}: SalesTableStateProps) {
  const isError = variant === 'error';
  const Icon = isError ? AlertTriangle : Inbox;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-6 py-14 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-12 w-12 items-center justify-center rounded-full',
          isError ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {description && <p className="max-w-sm text-xs text-muted-foreground">{description}</p>}
      {isError && onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
          Try again
        </Button>
      )}
      {!isError && action && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{action}</div>
      )}
    </div>
  );
}
